import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Tooltip } from 'components/common';
import Avatar, { AvatarGroup } from 'components/common/Avatar';
import InteractionTime from './InteractionTime';
import { FaClock } from 'react-icons/fa';
import { MdOutlineQuestionAnswer, MdBusiness, MdPeople } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import { getTopicResponses, onToggleResponses } from 'redux/crm/project/interactionSlice';
import ResponseForm from './ResponseForm';
import ResponseCard from './ResponseCard';
import RichContent from 'components/common/RichContent';

// ── Section label ─────────────────────────────────────────────────
const SectionLabel = ({ icon: Icon, children }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: 5,
    fontSize: 10, fontWeight: 700, letterSpacing: '0.7px',
    textTransform: 'uppercase', color: '#64748b', marginBottom: 8,
  }}>
    {Icon && <Icon size={12} />}
    {children}
  </div>
);
SectionLabel.propTypes = { icon: PropTypes.elementType, children: PropTypes.node };

const TopicCard = ({ topic }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const dispatch = useDispatch();
  const projectId = topic.project?.id;
  const topicId   = topic?.id;

  const { responses = [], responseLoading = [] } = useSelector((s) => s.crm_project_interaction || {});

  // Toggle/load once per topic: loadTopicResponses flips visibility and is redefined each
  // render, so depending on it would re-toggle (open/close) the responses on every render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadTopicResponses(); }, [topicId]);

  const loadTopicResponses = () => {
    const current   = responses[topicId];
    dispatch(onToggleResponses(topicId));
    const isOpening = !current?.visible;
    const notLoaded = !current?.loaded;
    if (isOpening && notLoaded) {
      dispatch(getTopicResponses({ projectId, topicId, params: new URLSearchParams({ page: '1', per_page: '5' }).toString() }));
    }
  };

  const handleLoadMore = (tid) => {
    const topicResponses = responses[tid];
    if (!topicResponses?.paginate) return;
    const nextPage = topicResponses.paginate.current_page + 1;
    dispatch(getTopicResponses({ projectId, topicId: tid, params: new URLSearchParams({ page: String(nextPage), per_page: '5' }).toString() }));
  };

  const { allow_create_response } = topic.permissions || {};

  const hasBuildings  = topic.buildings?.length > 0;
  const hasRecipients = topic.recipient_names?.length > 0;

  return (
    <div style={{ padding: '20px 20px 24px', background: '#f1f5f9' }}>

      {/* ── Description ────────────────────────────────────────── */}
      <div style={{ marginBottom: 16 }}>
        <SectionLabel>Description</SectionLabel>
        <div style={{
          background: '#fff', borderRadius: 10,
          border: '1px solid #e2e8f0',
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
          padding: '14px 16px',
          fontSize: '0.9rem', lineHeight: 1.75, color: '#374151',
          minHeight: 48,
        }}>
          {/* Sanitize + ảnh thumbnail/lightbox như ProjectTopicCard — trước đây
              description đi thẳng vào innerHTML chưa lọc. */}
          {topic?.description
            ? <RichContent html={topic.description} />
            : <span style={{ color: '#94a3b8' }}>No description provided.</span>}
        </div>
      </div>

      {/* ── Buildings + Recipients ──────────────────────────────── */}
      {(hasBuildings || hasRecipients) && (
        <div style={{
          display: 'grid', gridTemplateColumns: hasBuildings && hasRecipients ? '1fr 1fr' : '1fr',
          gap: 12, marginBottom: 16,
        }}>
          {hasBuildings && (
            <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', padding: '12px 14px' }}>
              <SectionLabel icon={MdBusiness}>Buildings</SectionLabel>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                {[...topic.buildings].sort((a, b) => a.name.localeCompare(b.name)).map((b) => (
                  <span key={b.id} style={{
                    background: '#e0f2fe', color: '#0369a1', border: '1px solid #bae6fd',
                    fontWeight: 600, fontSize: 11, padding: '3px 9px', borderRadius: 12,
                  }}>
                    {b.name}
                  </span>
                ))}
              </div>
            </div>
          )}

          {hasRecipients && (
            <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', padding: '12px 14px' }}>
              <SectionLabel icon={MdPeople}>Recipients</SectionLabel>
              <AvatarGroup>
                {topic.recipient_names.map((name, i) => (
                  <Tooltip key={i} placement="top" content={name}>
                    <Avatar name={name} size="m" />
                  </Tooltip>
                ))}
              </AvatarGroup>
            </div>
          )}
        </div>
      )}

      {/* ── Activity footer + Create response ──────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: '#94a3b8' }}>
          <FaClock size={10} />
          Last activity: <InteractionTime date={topic.last_activity_at} />
        </span>

        {allow_create_response && (
          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '6px 14px', borderRadius: 8,
              border: '1px solid #e2e8f0',
              background: isExpanded ? '#1d4ed8' : '#fff',
              color: isExpanded ? '#fff' : '#1d4ed8',
              fontSize: 12, fontWeight: 600, cursor: 'pointer',
              transition: 'all 0.15s',
            }}
          >
            <MdOutlineQuestionAnswer size={14} />
            {isExpanded ? 'Hide response form' : 'Create response'}
          </button>
        )}
      </div>

      {/* ── Response form ──────────────────────────────────────── */}
      {allow_create_response && (
        <ResponseForm open={isExpanded} topic={topic} />
      )}

      {/* ── Responses ──────────────────────────────────────────── */}
      <div style={{
        borderTop: '2px dashed #e2e8f0',
        paddingTop: 16, marginTop: 4,
      }}>
        <SectionLabel>Responses</SectionLabel>
        <ResponseCard
          topic={topic}
          responses={responses[topic.id] || []}
          isResponseLoading={responseLoading[topic.id] || false}
          onLoadMore={handleLoadMore}
        />
      </div>
    </div>
  );
};

TopicCard.propTypes = { topic: PropTypes.object.isRequired };

export default TopicCard;
